import { SHORTCUT_REGISTRY, type ShortcutId } from '@/lib/shortcutRegistry'
import { shortcut } from '@/lib/shortcuts'

export interface ShortcutGroup {
	name: string
	items: { id: string; label: string; keys: string }[]
}

/** Renders one SHORTCUT_REGISTRY entry as a help-dialog row, under the row id this dialog already used. */
function registryItem(rowId: string, shortcutId: ShortcutId) {
	const definition = SHORTCUT_REGISTRY[shortcutId]
	return { id: rowId, label: definition.label, keys: shortcut(definition.keys) }
}

/** A command with a Mod combo plus a single-key alternate (`Mod+/ or ?`, `Mod+B or [`). */
function registryItemWithAlt(rowId: string, primaryId: ShortcutId, altId: ShortcutId) {
	const primary = SHORTCUT_REGISTRY[primaryId]
	const alt = SHORTCUT_REGISTRY[altId]
	return { id: rowId, label: primary.label, keys: `${shortcut(primary.keys)} or ${shortcut(alt.keys)}` }
}

// General and "Tables and forms"' select-all/select-row/open-row entries are read from
// SHORTCUT_REGISTRY, the same source the keydown handlers bind against, so they can't drift out of
// sync the way a hand-typed copy can. Dashboards/Filters/Text editor stay hand-maintained: those
// bindings are owned by their own components, not by the registry.
export const SHORTCUT_GROUPS: ShortcutGroup[] = [
	{
		name: 'General',
		items: [
			registryItem('palette', 'palette.toggle'),
			registryItemWithAlt('sidebar', 'sidebar.toggle', 'sidebar.toggleAlt'),
			registryItem('header', 'header.toggle'),
			registryItem('focus', 'focusMode.toggle'),
			registryItemWithAlt('shortcuts', 'shortcuts.toggle', 'shortcuts.toggleAlt'),
			registryItem('create', 'create.open'),
			registryItem('search', 'search.focus'),
			registryItem('logout', 'auth.logout'),
		],
	},
	{
		name: 'Go to',
		items: [
			{ id: 'go-device-management', label: 'Device Management', keys: 'G then D' },
			{ id: 'go-dashboards', label: 'Dashboards', keys: 'G then B' },
			{ id: 'go-exporters', label: 'Exporters', keys: 'G then E' },
			{ id: 'go-remote-commands', label: 'Remote Commands', keys: 'G then R' },
			{ id: 'go-sessions', label: 'Sessions', keys: 'G then F' },
			{ id: 'go-ota-campaigns', label: 'OTA Campaigns', keys: 'G then C' },
			{ id: 'go-session-rules', label: 'Session Rules', keys: 'G then U' },
			{ id: 'go-assets', label: 'Assets', keys: 'G then A' },
			{ id: 'go-settings', label: 'Settings', keys: 'G then S' },
			{ id: 'go-org-inventory', label: 'Organization Inventory', keys: 'G then O' },
			{ id: 'go-project-home', label: "Current project's home", keys: 'G then P' },
			{ id: 'settings-general', label: 'Settings: General', keys: 'S then G' },
			{ id: 'settings-users', label: 'Settings: Users', keys: 'S then U' },
			{ id: 'settings-roles', label: 'Settings: Roles', keys: 'S then R' },
			{ id: 'settings-api-keys', label: 'Settings: API Keys', keys: 'S then K' },
			{ id: 'settings-metadata', label: 'Settings: Metadata', keys: 'S then M' },
			{ id: 'settings-device-state', label: 'Settings: Device State', keys: 'S then S' },
			{ id: 'settings-action', label: 'Settings: Action Settings', keys: 'S then A' },
			{ id: 'settings-streams', label: 'Settings: Streams', keys: 'S then T' },
			{ id: 'settings-modules', label: 'Settings: Modules', keys: 'S then D' },
			{ id: 'settings-subscriber-groups', label: 'Settings: Subscriber Groups', keys: 'S then B' },
			{ id: 'settings-anomalies', label: 'Settings: Anomalies', keys: 'S then N' },
			{ id: 'settings-audit-logs', label: 'Settings: Audit Logs', keys: 'S then L' },
			{ id: 'switch-project', label: 'Switch project', keys: 'S then P' },
			{ id: 'switch-organization', label: 'Switch organization', keys: 'S then O' },
		],
	},
	{
		name: 'Device Management',
		items: [
			{ id: 'device-update-firmware', label: 'Update Firmware', keys: 'U then F' },
			{ id: 'device-update-config', label: 'Update Config', keys: 'U then C' },
			{ id: 'device-update-geofence', label: 'Update Geofence', keys: 'U then G' },
			{ id: 'device-update-metadata', label: 'Update Metadata', keys: 'U then M' },
			{ id: 'device-send-file', label: 'Send File', keys: 'U then S' },
		],
	},
	{
		name: 'Dashboards',
		items: [
			{ id: 'panel-scroll', label: 'Scroll inside a panel', keys: `${shortcut('Mod')} + scroll` },
			{ id: 'panel-move', label: 'Move selected panel', keys: 'Arrow keys' },
			{ id: 'panel-resize', label: 'Resize selected panel', keys: `${shortcut('Shift')} + arrow keys` },
			{ id: 'panel-full-page', label: 'Full page, or exit it', keys: 'F' },
			{ id: 'panel-edit', label: 'Edit panel', keys: 'E' },
			{ id: 'panel-clone', label: 'Clone panel', keys: `${shortcut('Shift')} + D` },
			{ id: 'panel-delete', label: 'Delete panel', keys: shortcut('Mod-Backspace') },
			{ id: 'panel-download', label: 'Download data', keys: shortcut('Alt-Shift-D') },
		],
	},
	{
		name: 'Tables and forms',
		items: [
			registryItem('select-all', 'table.selectAll'),
			registryItem('select-row', 'table.toggleHoveredRow'),
			registryItem('open-row', 'table.openHoveredRow'),
			registryItem('open-active-row', 'table.openActiveRow'),
			{ id: 'extend-selection', label: 'Extend the selection', keys: `${shortcut('Shift')} + arrow keys` },
			registryItem('clear-active-or-selection', 'table.clearActiveOrSelection'),
			{ id: 'next-field', label: 'Next field', keys: 'Enter' },
			{ id: 'submit', label: 'Submit form', keys: shortcut('Mod-Enter') },
		],
	},
	{
		name: 'Filters',
		items: [
			{ id: 'filter-values', label: 'Go to values', keys: shortcut('Ctrl-L') },
			{ id: 'filter-back', label: 'Back to properties', keys: shortcut('Ctrl-H') },
		],
	},
	{
		name: 'Text editor',
		items: [
			{ id: 'bold', label: 'Bold', keys: shortcut('Mod-B') },
			{ id: 'italic', label: 'Italic', keys: shortcut('Mod-I') },
			{ id: 'underline', label: 'Underline', keys: shortcut('Mod-U') },
			{ id: 'strike', label: 'Strikethrough', keys: shortcut('Mod-Shift-S') },
			{ id: 'code', label: 'Inline code', keys: shortcut('Mod-E') },
			{ id: 'link', label: 'Link', keys: shortcut('Mod-K') },
			{ id: 'undo', label: 'Undo', keys: shortcut('Mod-Z') },
			{ id: 'redo', label: 'Redo', keys: shortcut('Mod-Shift-Z') },
		],
	},
]

/** A group name match keeps the whole group; otherwise only the matching shortcuts stay. */
export function filterShortcuts(groups: ShortcutGroup[], query: string): ShortcutGroup[] {
	const needle = query.trim().toLowerCase()
	if (!needle) {
		return groups
	}
	return groups
		.map(group =>
			group.name.toLowerCase().includes(needle)
				? group
				: { ...group, items: group.items.filter(item => item.label.toLowerCase().includes(needle)) },
		)
		.filter(group => group.items.length > 0)
}
